import { administrationClientPermissions, administrationPermissions } from "./administrationPermissions";
import { assistantPermissions } from "./assistantPermissions";
import { budgetPermissions } from "./budgetPermissions";
import { getDashboardPermissions } from "./dashboardPermissions";
import { getEventsPermissions } from "./eventsPermissions";
import { filesPermissions } from "./filesPermissions";
import { getImsPermissions } from "./imsPermissions";
import {
  learningAdminRoles,
  learningResultsRoles,
  learningViewerRoles,
} from "../tw/learning/admin/learningPermissions";
import { getMedicalPermissions } from "./medicalPermissions";
import { getRequestsPermissions } from "./requestsPermissions";
import {
  getSalesClientPermissions,
  getSalesItemPermissions,
  getSalesPermissions,
  getSalesSummaryPermissions,
} from "./salesPermissions";
import {
  getSummaryItemPermissions,
  getSummaryServicesPermissions,
} from "./summariesPermissions";
import { warehouseItemPermissions } from "./warehousePermissions";
import { getArticoliEventoViewPermissions } from "./articoliEventoPermissions";

const mergePermissions = (...permissionGroups) => [
  ...new Set(permissionGroups.reduce((permissions, group) => permissions.concat(group), [])),
];

const intersectPermissions = (sectionPermissions, itemPermissions) => [
  ...sectionPermissions.filter(
    (permission) =>
      typeof permission === "number" && itemPermissions.includes(permission)
  ),
  sectionPermissions.includes(true) && itemPermissions.includes(true),
];

const normalizeHash = (hash) => {
  if (!hash) {
    return "";
  }

  const hashValue = String(hash).replace(/^#+/, "").replace(/^\/+/, "");
  return hashValue ? `#/${hashValue}` : "";
};

const normalizeRoute = (path, currentHash) => {
  const route = String(path || "/");
  const separatorIndex = route.indexOf("#");
  const pathname = separatorIndex === -1 ? route : route.slice(0, separatorIndex);
  const hash = separatorIndex === -1 ? currentHash : route.slice(separatorIndex);
  const normalizedPathname = pathname.replace(/\/+$/, "") || "/";

  return `${normalizedPathname}${normalizeHash(hash)}`;
};

const getSalesClientRoutePermissions = (userIsAgente, user) => {
  const salesClientPermissions = intersectPermissions(
    getSalesPermissions(userIsAgente, user),
    getSalesClientPermissions(userIsAgente)
  );

  return mergePermissions(administrationClientPermissions, salesClientPermissions);
};

const routePermissionGetters = {
  "/dashboard": ({ userIsAgente }) => getDashboardPermissions(userIsAgente),
  "/ai-assistant": () => assistantPermissions,
  "/budget": () => budgetPermissions,
  "/learning-videos": () => learningViewerRoles,
  "/admin/learning-results": () => learningResultsRoles,
  "/ims": ({ userIsAgente }) => getImsPermissions(userIsAgente),
  "/ims-videoguida": ({ userIsAgente }) => getImsPermissions(userIsAgente),
  "/admin": () => mergePermissions(administrationPermissions, learningAdminRoles),
  "/medici": ({ userIsAgente }) => getMedicalPermissions(userIsAgente),
  "/centri": ({ userIsAgente }) => getMedicalPermissions(userIsAgente),
  "/centri/medici": ({ userIsAgente }) => getMedicalPermissions(userIsAgente),
  "/gestione-campioni": () => warehouseItemPermissions,
  "/gestione-materiale": () => warehouseItemPermissions,
  "/gestione-prodotti": () => warehouseItemPermissions,
  "/richiesta-campioni": ({ userIsAgente }) => getRequestsPermissions(userIsAgente),
  "/richiesta-materiale": ({ userIsAgente }) => getRequestsPermissions(userIsAgente),
  "/servizi": ({ userIsAgente }) => getRequestsPermissions(userIsAgente),
  "/status-campionatura-servizi": ({ userIsAgente, hash }) =>
    hash === "#/servizi"
      ? getSummaryServicesPermissions(userIsAgente)
      : getSummaryItemPermissions(userIsAgente),
  "/richiesta-prodotti": ({ userIsAgente, user }) =>
    intersectPermissions(
      getSalesPermissions(userIsAgente, user),
      getSalesItemPermissions(userIsAgente)
    ),
  "/status-vendita": ({ userIsAgente, user }) =>
    intersectPermissions(
      getSalesPermissions(userIsAgente, user),
      getSalesSummaryPermissions(userIsAgente)
    ),
  "/clienti": ({ userIsAgente, user }) =>
    getSalesClientRoutePermissions(userIsAgente, user),
  "/file": () => filesPermissions,
  "/calendario": ({ userIsAgente }) => getEventsPermissions(userIsAgente),
  "/agenda": ({ userIsAgente }) => getEventsPermissions(userIsAgente),
  "/campioni-eventi": ({ userIsAgente }) => getArticoliEventoViewPermissions(userIsAgente),
};

export const resolveRoutePermissions = (
  path,
  userIsAgente,
  user = {},
  currentHash = ""
) => {
  const route = normalizeRoute(path, currentHash);
  const pathname = route.split("#")[0];
  const routePermissionGetter = routePermissionGetters[pathname];

  if (!routePermissionGetter) {
    return undefined;
  }

  return routePermissionGetter({
    userIsAgente: Boolean(userIsAgente),
    user: user && typeof user === "object" ? user : {},
    hash: normalizeHash(route.split("#")[1]),
  });
};

export const getRoutePermissions = resolveRoutePermissions;

export const isRoutePermissionAllowed = (allowedRoles, user) => {
  if (typeof allowedRoles === "undefined") {
    return true;
  }

  if (allowedRoles === true) {
    return true;
  }

  if (allowedRoles === false || !Array.isArray(allowedRoles)) {
    return false;
  }

  if (allowedRoles.includes(true)) {
    return true;
  }

  const role = parseInt(user?.idRole, 10);
  return !isNaN(role) && allowedRoles.includes(role);
};
